import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

type Politeness = 'polite' | 'assertive';
type Announce = (message: string, politeness?: Politeness) => void;

const LiveRegionContext = createContext<Announce | null>(null);

/**
 * One polite and one assertive visually-hidden live region for the whole app.
 * Screen reader users hear async results ("12 results", "Item checked in") through useAnnounce().
 */
export function LiveRegionProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const flip = useRef(false);
  const clearTimer = useRef<number | undefined>(undefined);

  const announce = useCallback<Announce>((message, politeness = 'polite') => {
    // Alternate a trailing non-breaking space so repeating the same message is still announced.
    flip.current = !flip.current;
    const text = flip.current ? message : `${message}\u00a0`;
    if (politeness === 'assertive') {
      setAssertive(text);
    } else {
      setPolite(text);
    }
    window.clearTimeout(clearTimer.current);
    clearTimer.current = window.setTimeout(() => {
      setPolite('');
      setAssertive('');
    }, 6000);
  }, []);

  useEffect(() => () => window.clearTimeout(clearTimer.current), []);

  const value = useMemo(() => announce, [announce]);
  return (
    <LiveRegionContext.Provider value={value}>
      {children}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {polite}
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        {assertive}
      </div>
    </LiveRegionContext.Provider>
  );
}

/** Announce a message to assistive tech. A no-op outside the provider so components stay testable. */
export function useAnnounce(): Announce {
  return useContext(LiveRegionContext) ?? (() => undefined);
}

/** Alias matching the hook name in the design brief. */
export const useLiveRegion = useAnnounce;
